import { audio } from '../../../../audio/AudioManager.js';
import { SaveManager } from '../../../../state/SaveManager.js';
import { applicationLifecycle } from '../../../shared/lifecycle/index.js';
import { ensureMinimumTextTouchTarget } from '../../../ui/TouchTarget.js';
import { DISCOVERY_LAB_ASSETS } from '../DiscoveryLabAssets.js';
import { DISCOVERY_LAB_DISTRICTS, DISCOVERY_LAB_LAYOUT } from '../DiscoveryLabConfig.js';
import {
  calculateDiscoveryLabSafeBounds,
  isCompactDiscoveryLabLayout,
} from '../components/DiscoveryLabLayout.js';
import {
  getDiscoveryLabState,
  visitDiscoveryLabDistrict,
} from '../DiscoveryLabState.js';
import type {
  MaterialsInvestigationSnapshot,
} from '../minigames/materials/MaterialsInvestigationState.js';
import {
  MATERIALS_WORKSHOP_ASSET_REQUIREMENTS,
  canEnterMaterialsWorkshop,
  getMissingMaterialsWorkshopAssets,
} from './MaterialsWorkshopAssets.js';
import { MATERIALS_WORKSHOP_ROOF_1 } from './MaterialsWorkshopConfig.js';
import { enterMaterialsWorkshopInvestigation } from './MaterialsWorkshopInvestigation.js';
import type { MaterialsInvestigation } from '../minigames/materials/MaterialsInvestigation.js';
import { canEnterWeatherWatchStation } from '../weather-watch-station/WeatherWatchAssets.js';

export const MATERIALS_WORKSHOP_SCENE_KEY = 'MaterialsWorkshop';

const FONT_UI = '"Nunito", "Segoe UI", Arial, sans-serif';
type MaterialTestId = 'water-test' | 'bend-test' | 'light-test';
const ASSET = Object.fromEntries(
  MATERIALS_WORKSHOP_ASSET_REQUIREMENTS.map(asset => [asset.key, asset]),
) as Record<string, (typeof MATERIALS_WORKSHOP_ASSET_REQUIREMENTS)[number]>;

interface MaterialView {
  readonly id: string;
  readonly image: Phaser.GameObjects.Image;
  readonly label: Phaser.GameObjects.Text;
  readonly marker: Phaser.GameObjects.Image;
}

interface StationView {
  readonly id: MaterialTestId;
  readonly image: Phaser.GameObjects.Image;
  readonly label: Phaser.GameObjects.Text;
}

interface ArtButton {
  readonly image: Phaser.GameObjects.Image;
  readonly label: Phaser.GameObjects.Text;
}

export class MaterialsWorkshopScene extends Phaser.Scene {
  static readonly requiredImageKeys = MATERIALS_WORKSHOP_ASSET_REQUIREMENTS.map(asset => asset.key);

  private investigation!: MaterialsInvestigation;
  private snapshot!: MaterialsInvestigationSnapshot;
  private background!: Phaser.GameObjects.Image;
  private workbench!: Phaser.GameObjects.Image;
  private shelterDamaged!: Phaser.GameObjects.Image;
  private shelterRepaired!: Phaser.GameObjects.Image;
  private foreground!: Phaser.GameObjects.Image;
  private buildStation!: Phaser.GameObjects.Image;
  private missionPanel!: Phaser.GameObjects.Image;
  private evidencePanel!: Phaser.GameObjects.Image;
  private cosmo!: Phaser.GameObjects.Image;
  private titleText!: Phaser.GameObjects.Text;
  private phaseText!: Phaser.GameObjects.Text;
  private missionText!: Phaser.GameObjects.Text;
  private evidenceText!: Phaser.GameObjects.Text;
  private feedbackText!: Phaser.GameObjects.Text;
  private controlsText!: Phaser.GameObjects.Text;
  private backText!: Phaser.GameObjects.Text;
  private homeText!: Phaser.GameObjects.Text;
  private nextText!: Phaser.GameObjects.Text;
  private materials: MaterialView[] = [];
  private stations: StationView[] = [];
  private testButton!: ArtButton;
  private hintButton!: ArtButton;
  private resetButton!: ArtButton;
  private applyButton!: ArtButton;
  private selectedMaterialId?: string;
  private selectedTestId?: MaterialTestId;
  private reducedMotion = false;
  private completedOnEntry = false;
  private inputEnabled = true;
  private leaving = false;
  private renderScale = 1;
  private resizeTimer?: number;
  private unsubscribeLifecycle?: () => void;

  constructor() { super({ key: MATERIALS_WORKSHOP_SCENE_KEY }); }

  create() {
    this.materials = [];
    this.stations = [];
    this.selectedMaterialId = undefined;
    this.selectedTestId = undefined;
    this.inputEnabled = true;
    this.leaving = false;

    const world = getDiscoveryLabState();
    const missing = getMissingMaterialsWorkshopAssets(this.textures);
    if (!world.unlockedDistricts.includes(DISCOVERY_LAB_DISTRICTS.pollinatorGarden)
      || world.pollinatorGardenRestoration !== 'restored'
      || !canEnterMaterialsWorkshop(this.textures)) {
      if (missing.length > 0) {
        console.error('[MaterialsWorkshop] Required production textures are missing:',
          missing.map(asset => `${asset.key} (${asset.path})`));
      }
      this.scene.start('DiscoveryLab');
      return;
    }

    const entry = enterMaterialsWorkshopInvestigation();
    if (!entry.available) {
      console.warn('[MaterialsWorkshop] Route unavailable:', entry.reason);
      this.scene.start('DiscoveryLab');
      return;
    }

    void audio.enterSoundscape('discovery-lab');
    this.renderScale = this.configureRenderer();
    this.reducedMotion = SaveManager.getSettings().reducedMotion
      || (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
    this.inputEnabled = true;
    this.leaving = false;
    this.completedOnEntry = world.materialsWorkshopRestoration === 'restored';
    this.investigation = entry.investigation;
    this.snapshot = this.investigation.getSnapshot();
    SaveManager.setLastScene(MATERIALS_WORKSHOP_SCENE_KEY);
    SaveManager.save();

    this.buildEnvironment();
    this.buildInterface();
    this.registerInput();
    this.registerLifecycle();
    this.refresh(this.snapshot);
    this.layout();
    this.applyTextResolution();
    this.cameras.main.fadeIn(this.reducedMotion ? 160 : 460, 180, 235, 225);
  }

  private buildEnvironment() {
    this.background = this.add.image(640, 360, this.key('materials-workshop-background'))
      .setDisplaySize(1280, 720).setDepth(0);
    this.workbench = this.add.image(640, 360, this.key('materials-workshop-workbench'))
      .setDisplaySize(1280, 720).setDepth(3);
    this.shelterDamaged = this.add.image(1080, 610, this.key('materials-workshop-shelter-damaged'))
      .setOrigin(0.5, 1).setDepth(9);
    this.shelterRepaired = this.add.image(1080, 610, this.key('materials-workshop-shelter-repaired'))
      .setOrigin(0.5, 1).setDepth(10).setAlpha(0);
    this.foreground = this.add.image(640, 360, this.key('materials-workshop-foreground'))
      .setDisplaySize(1280, 720).setDepth(90);
    this.buildStation = this.add.image(1040, 535, this.key('materials-workshop-build-station'))
      .setDepth(12);
    this.cosmo = this.add.image(110, 650, DISCOVERY_LAB_ASSETS.cosmo.key).setDepth(96);
  }

  private buildInterface() {
    this.missionPanel = this.add.image(640, 105, this.key('materials-workshop-mission-panel')).setDepth(20);
    this.evidencePanel = this.add.image(640, 520, this.key('materials-workshop-observation-panel')).setDepth(20);
    this.titleText = this.makeText('MATERIALS WORKSHOP', 28, '#e9ffff', 'bold')
      .setStroke('#063e49', 7).setDepth(108);
    this.phaseText = this.makeText('', 13, '#d9ffff', 'bold')
      .setStroke('#063e49', 5).setDepth(108);
    this.missionText = this.makeText('', 17, '#25454a', 'bold')
      .setWordWrapWidth(580).setDepth(30);
    this.evidenceText = this.makeText('', 14, '#25454a', 'bold')
      .setWordWrapWidth(570).setLineSpacing(4).setDepth(30);
    this.feedbackText = this.makeText('', 14, '#ffffff', 'bold')
      .setStroke('#063e49', 5).setWordWrapWidth(720).setDepth(108);
    this.controlsText = this.makeText(
      '1–3 material  •  W water  •  B bend  •  L light  •  Enter test/record/continue  •  H hint  •  R reset',
      12,
      '#ffffff',
      'bold',
    ).setStroke('#063e49', 5).setDepth(108);
    this.backText = this.makeText('←  POLLINATOR GARDEN', 15, '#ffffff', 'bold')
      .setStroke('#063e49', 6).setInteractive({ useHandCursor: true }).setDepth(109);
    this.homeText = this.makeText('HOME HUB  (M)', 15, '#ffffff', 'bold')
      .setStroke('#063e49', 6).setInteractive({ useHandCursor: true }).setDepth(109);
    [this.backText, this.homeText].forEach(target => ensureMinimumTextTouchTarget(target));
    this.backText.on('pointerup', this.returnGarden, this);
    this.homeText.on('pointerup', this.returnHome, this);
    this.nextText = this.makeText('WEATHER WATCH STATION • NEXT', 14, '#d7fbff', 'bold')
      .setStroke('#063e49', 6).setInteractive({ useHandCursor: true }).setDepth(109);
    ensureMinimumTextTouchTarget(this.nextText);
    this.nextText.on('pointerup', this.tryWeatherWatch, this);

    this.materials = MATERIALS_WORKSHOP_ROOF_1.materials.map(material => {
      const image = this.add.image(0, 0, this.materialTexture(material.id, 'base'))
        .setDepth(35).setInteractive({ useHandCursor: true });
      const label = this.makeText(material.label, 13, '#ffffff', 'bold')
        .setStroke('#063e49', 5).setDepth(37);
      const marker = this.add.image(0, 0, this.key('materials-workshop-evidence-marker'))
        .setDepth(38).setVisible(false);
      image.on('pointerup', () => this.selectMaterial(material.id));
      return { id: material.id, image, label, marker };
    });

    const stationDefinitions: readonly [MaterialTestId, string, string][] = [
      ['water-test', 'materials-workshop-water-station', 'WATER'],
      ['bend-test', 'materials-workshop-bend-station', 'BEND'],
      ['light-test', 'materials-workshop-light-station', 'LIGHT'],
    ];
    this.stations = stationDefinitions.map(([id, key, label]) => {
      const image = this.add.image(0, 0, this.key(key)).setDepth(34).setInteractive({ useHandCursor: true });
      const text = this.makeText(label, 12, '#ffffff', 'bold').setStroke('#063e49', 5).setDepth(36);
      image.on('pointerup', () => this.selectTest(id));
      return { id, image, label: text };
    });

    this.testButton = this.makeButton('materials-workshop-test-button', 'CONTINUE', () => this.primaryAction());
    this.hintButton = this.makeButton('materials-workshop-hint-button', 'HINT', () => this.hint());
    this.resetButton = this.makeButton('materials-workshop-reset-button', 'RESET', () => this.reset());
    this.applyButton = this.makeButton('materials-workshop-apply-button', 'APPLY MATERIAL', () => this.applyMaterial());
  }

  private key(key: string) { return ASSET[key].key; }

  private materialTexture(materialId: string, state: 'base' | MaterialTestId) {
    return this.key(`materials-workshop-${materialId}-${state}`);
  }

  private makeText(value: string, size: number, color: string, style: string) {
    return this.add.text(0, 0, value, {
      fontFamily: FONT_UI,
      fontSize: `${size}px`,
      fontStyle: style,
      color,
      align: 'center',
    }).setOrigin(0.5);
  }

  private makeButton(key: string, label: string, action: () => void): ArtButton {
    const image = this.add.image(0, 0, this.key(key)).setDepth(104).setInteractive({ useHandCursor: true });
    const text = this.makeText(label, 12, '#ffffff', 'bold').setStroke('#063e49', 4).setDepth(105);
    image.on('pointerup', action);
    return { image, label: text };
  }

  private selectMaterial(id: string) {
    if (!this.inputEnabled || this.snapshot.phase === 'complete') return;
    this.selectedMaterialId = id;
    if (this.snapshot.phase === 'inspect-materials') {
      this.investigation.inspectMaterial(id);
    } else if (this.snapshot.phase === 'predict') {
      this.investigation.choosePrediction(id);
    } else if (this.snapshot.phase === 'choose-material') {
      const result = this.investigation.chooseMaterial(id);
      if (result) {
        this.feedbackText.setText(result.feedback);
        if (result.suitedToPurpose) audio.correct();
        else audio.wrong();
      }
    }
    this.captureSnapshot();
    this.playSelectionFeedback(id);
  }

  private selectTest(id: MaterialTestId) {
    if (this.snapshot.phase !== 'test-properties' || this.snapshot.pendingObservation) return;
    this.selectedTestId = id;
    const definition = MATERIALS_WORKSHOP_ROOF_1.tests.find(test => test.id === id);
    if (definition) this.feedbackText.setText(definition.modelDescription);
    this.refresh(this.snapshot);
    audio.click();
  }

  private primaryAction() {
    if (!this.inputEnabled) return;
    const phase = this.snapshot.phase;
    if (phase === 'inspect-materials') {
      if (!this.investigation.beginPrediction()) {
        this.feedbackText.setText('Inspect all three samples before making a prediction.');
        audio.wrong();
      }
    } else if (phase === 'test-properties') {
      if (this.snapshot.pendingObservation) {
        const observation = this.investigation.recordObservation();
        if (observation) {
          this.feedbackText.setText(`${observation.observation} Evidence recorded.`);
          audio.correct();
        }
      } else if (this.allTestsRecorded()) {
        this.investigation.beginComparison();
      } else if (this.selectedMaterialId && this.selectedTestId) {
        const observation = this.investigation.runTest(this.selectedMaterialId, this.selectedTestId);
        if (observation) {
          this.feedbackText.setText(observation.observation);
          this.playTestFeedback();
        } else {
          this.feedbackText.setText('That test is already recorded. Choose another material or model.');
        }
      } else {
        this.feedbackText.setText('Choose a material and a safe model station first.');
      }
    } else if (phase === 'compare-evidence') {
      this.investigation.beginMaterialChoice();
    }
    this.captureSnapshot();
  }

  private applyMaterial() {
    if (this.snapshot.phase !== 'build-repair') return;
    if (this.investigation.applyMaterial()) {
      this.captureSnapshot();
      this.playRestoration();
    }
  }

  private hint() {
    const hint = this.investigation.requestHint();
    if (hint) {
      this.feedbackText.setText(hint);
      audio.hint();
      this.captureSnapshot();
    }
  }

  private reset() {
    this.investigation.reset();
    this.selectedMaterialId = undefined;
    this.selectedTestId = undefined;
    this.feedbackText.setText('Investigation reset. Inspect each material to begin again.');
    this.captureSnapshot();
    audio.click();
  }

  private captureSnapshot() {
    this.snapshot = this.investigation.getSnapshot();
    this.refresh(this.snapshot);
  }

  private allTestsRecorded() {
    return MATERIALS_WORKSHOP_ROOF_1.materials.every(material =>
      MATERIALS_WORKSHOP_ROOF_1.tests.every(test =>
        this.snapshot.completedTestKeys.includes(`${material.id}:${test.id}`)));
  }

  private refresh(snapshot: MaterialsInvestigationSnapshot) {
    this.snapshot = snapshot;
    const persistedCompletion = this.completedOnEntry && snapshot.phase === 'inspect-materials';
    const phaseLabel = persistedCompletion
      ? 'COMPLETE'
      : snapshot.phase.replace(/-/g, ' ').toLocaleUpperCase();
    this.phaseText.setText(phaseLabel);
    this.missionText.setText(snapshot.phase === 'complete' || persistedCompletion
      ? MATERIALS_WORKSHOP_ROOF_1.task.buildResult
      : MATERIALS_WORKSHOP_ROOF_1.task.problem);

    this.materials.forEach(view => {
      const pending = snapshot.pendingObservation?.materialId === view.id
        ? snapshot.pendingObservation.testId as MaterialTestId
        : undefined;
      const observations = snapshot.observations.filter(item => item.materialId === view.id);
      const latest = observations.at(-1)?.testId as MaterialTestId | undefined;
      const state = pending ?? latest ?? 'base';
      const selected = this.selectedMaterialId === view.id
        || snapshot.selectedMaterialId === view.id
        || snapshot.predictedMaterialId === view.id;
      view.image.setTexture(this.materialTexture(view.id, state)).setTint(selected ? 0xcaffff : 0xffffff);
      view.marker.setVisible(observations.length > 0).setAlpha(Math.min(1, 0.45 + observations.length * 0.18));
    });
    this.stations.forEach(view => view.image.setTint(this.selectedTestId === view.id ? 0xcaffff : 0xffffff));

    const lines = snapshot.observations.slice(-4).map(observation => `• ${observation.observation}`);
    this.evidenceText.setText(lines.length > 0
      ? lines.join('\n')
      : 'Evidence appears here after each safe in-game material model.');

    let primaryLabel = 'CONTINUE';
    if (snapshot.phase === 'test-properties') {
      primaryLabel = snapshot.pendingObservation ? 'RECORD EVIDENCE'
        : this.allTestsRecorded() ? 'COMPARE EVIDENCE'
          : 'RUN SAFE MODEL';
    } else if (snapshot.phase === 'compare-evidence') primaryLabel = 'CHOOSE MATERIAL';
    this.testButton.label.setText(primaryLabel);
    const restored = snapshot.phase === 'complete' || persistedCompletion;
    const interactivePhase = !restored;
    this.controlsText.setVisible(interactivePhase);
    this.testButton.image.setVisible(interactivePhase && snapshot.phase !== 'build-repair');
    this.testButton.label.setVisible(interactivePhase && snapshot.phase !== 'build-repair');
    const applying = snapshot.phase === 'build-repair';
    this.applyButton.image.setVisible(applying);
    this.applyButton.label.setVisible(applying);
    this.hintButton.image.setVisible(interactivePhase);
    this.hintButton.label.setVisible(interactivePhase);
    this.resetButton.image.setVisible(interactivePhase);
    this.resetButton.label.setVisible(interactivePhase);
    this.shelterDamaged.setAlpha(restored ? 0 : 1);
    this.shelterRepaired.setAlpha(restored ? 1 : 0);
    if (restored) this.feedbackText.setText(MATERIALS_WORKSHOP_ROOF_1.successMessage);
    else if (!this.feedbackText.text) this.feedbackText.setText(this.instructionFor(snapshot));
    this.nextText.setVisible(restored);
    this.layout();
  }

  private instructionFor(snapshot: MaterialsInvestigationSnapshot) {
    switch (snapshot.phase) {
      case 'inspect-materials': return 'Tap every material sample and notice how each looks and feels.';
      case 'predict': return MATERIALS_WORKSHOP_ROOF_1.predictionPrompt;
      case 'test-properties': return 'Select a sample and a safe model station, then run and record the observation.';
      case 'compare-evidence': return MATERIALS_WORKSHOP_ROOF_1.comparisonPrompt;
      case 'choose-material': return MATERIALS_WORKSHOP_ROOF_1.choicePrompt;
      case 'build-repair': return 'The evidence supports the sealed panel. Apply it at the build station.';
      case 'complete': return MATERIALS_WORKSHOP_ROOF_1.successMessage;
    }
  }

  private playSelectionFeedback(materialId: string) {
    const view = this.materials.find(candidate => candidate.id === materialId);
    if (!view) return;
    const effect = this.add.image(view.image.x, view.image.y, this.key('materials-workshop-selection-vfx'))
      .setDisplaySize(160, 120).setDepth(75).setBlendMode(Phaser.BlendModes.ADD)
      .setAlpha(this.reducedMotion ? 0.62 : 0);
    if (this.reducedMotion) this.time.delayedCall(500, () => effect.destroy());
    else this.tweens.add({ targets: effect, alpha: 0.78, duration: 300, yoyo: true, onComplete: () => effect.destroy() });
  }

  private playTestFeedback() {
    const effect = this.add.image(640, 400, this.key('materials-workshop-test-feedback-vfx'))
      .setDisplaySize(260, 195).setDepth(76).setBlendMode(Phaser.BlendModes.ADD)
      .setAlpha(this.reducedMotion ? 0.68 : 0);
    if (this.reducedMotion) this.time.delayedCall(650, () => effect.destroy());
    else this.tweens.add({ targets: effect, alpha: 0.86, duration: 380, yoyo: true, hold: 120, onComplete: () => effect.destroy() });
  }

  private playRestoration() {
    audio.restoration();
    const effect = this.add.image(this.shelterRepaired.x, this.shelterRepaired.y - 150, this.key('materials-workshop-restoration-vfx'))
      .setDisplaySize(430, 242).setDepth(78).setBlendMode(Phaser.BlendModes.ADD)
      .setAlpha(this.reducedMotion ? 0.76 : 0);
    if (this.reducedMotion) this.time.delayedCall(1000, () => effect.destroy());
    else this.tweens.add({
      targets: effect,
      alpha: 0.94,
      scaleX: 1.15,
      scaleY: 1.15,
      duration: 650,
      yoyo: true,
      hold: 220,
      onComplete: () => effect.destroy(),
    });
    audio.celebrate();
  }

  private registerInput() { this.input.keyboard?.on('keydown', this.handleKeyDown, this); }

  private handleKeyDown(event: KeyboardEvent) {
    if (!this.inputEnabled || this.leaving) return;
    const key = event.key.toLocaleLowerCase();
    if (key === 'escape') return this.returnGarden();
    if (key === 'm') return this.returnHome();
    if (key >= '1' && key <= '3') {
      const material = MATERIALS_WORKSHOP_ROOF_1.materials[Number(key) - 1];
      if (material) this.selectMaterial(material.id);
      return;
    }
    if (key === 'w') return this.selectTest('water-test');
    if (key === 'b') return this.selectTest('bend-test');
    if (key === 'l') return this.selectTest('light-test');
    if (key === 'enter' || key === ' ') return this.primaryAction();
    if (key === 'a') return this.applyMaterial();
    if (key === 'h') return this.hint();
    if (key === 'r') this.reset();
  }

  private layout() {
    const safe = calculateDiscoveryLabSafeBounds(this);
    const compact = isCompactDiscoveryLabLayout(this, safe);
    this.titleText.setPosition(safe.centerX, safe.y + (compact ? 26 : 34)).setFontSize(compact ? 22 : 28);
    this.phaseText.setPosition(safe.centerX, safe.y + (compact ? 49 : 61));
    this.backText.setPosition(safe.x + 90, safe.y + 26).setFontSize(compact ? 12 : 15);
    this.homeText.setPosition(safe.right - 66, safe.y + 26).setFontSize(compact ? 12 : 15);
    const missionW = compact ? 520 : 590;
    this.missionPanel.setPosition(safe.centerX, safe.y + (compact ? 104 : 115))
      .setDisplaySize(missionW, missionW * (240 / 720));
    this.missionText.setPosition(safe.centerX, safe.y + (compact ? 108 : 120))
      .setFontSize(compact ? 14 : 17).setWordWrapWidth(missionW * 0.75);

    const materialY = safe.y + (compact ? 255 : 285);
    const materialGap = compact ? 205 : 235;
    this.materials.forEach((view, index) => {
      const x = safe.centerX + (index - 1) * materialGap;
      view.image.setPosition(x, materialY).setDisplaySize(compact ? 126 : 146, compact ? 126 : 146);
      view.label.setPosition(x, materialY + (compact ? 78 : 90)).setFontSize(compact ? 11 : 13);
      view.marker.setPosition(x + (compact ? 54 : 63), materialY - (compact ? 52 : 60))
        .setDisplaySize(compact ? 42 : 48, compact ? 42 : 48);
    });

    const stationY = safe.y + (compact ? 400 : 435);
    const stationGap = compact ? 170 : 192;
    this.stations.forEach((view, index) => {
      const x = safe.centerX + (index - 1) * stationGap;
      view.image.setPosition(x, stationY).setDisplaySize(compact ? 118 : 136, compact ? 118 : 136);
      view.label.setPosition(x, stationY + (compact ? 66 : 76));
    });

    const evidenceY = safe.bottom - (compact ? 118 : 128);
    const evidenceW = Math.min(compact ? 600 : 670, safe.width - 330);
    this.evidencePanel.setPosition(safe.centerX, evidenceY)
      .setDisplaySize(evidenceW, evidenceW * (420 / 720));
    this.evidenceText.setPosition(safe.centerX, evidenceY).setFontSize(compact ? 11 : 13)
      .setWordWrapWidth(evidenceW * 0.74);
    this.feedbackText.setPosition(safe.centerX, evidenceY - (compact ? 72 : 80))
      .setFontSize(compact ? 12 : 14).setWordWrapWidth(Math.min(720, safe.width - 300));

    const controlY = safe.bottom - 38;
    this.testButton.image.setPosition(safe.centerX, controlY).setDisplaySize(compact ? 116 : 132, compact ? 58 : 66);
    this.testButton.label.setPosition(safe.centerX, controlY).setFontSize(compact ? 10 : 12);
    this.applyButton.image.setPosition(safe.centerX, controlY).setDisplaySize(compact ? 140 : 158, compact ? 70 : 79);
    this.applyButton.label.setPosition(safe.centerX, controlY).setFontSize(compact ? 10 : 12);
    this.hintButton.image.setPosition(safe.centerX - (compact ? 170 : 195), controlY).setDisplaySize(100, 50);
    this.hintButton.label.setPosition(this.hintButton.image.x, controlY);
    this.resetButton.image.setPosition(safe.centerX + (compact ? 170 : 195), controlY).setDisplaySize(100, 50);
    this.resetButton.label.setPosition(this.resetButton.image.x, controlY);
    this.controlsText.setPosition(safe.centerX, safe.bottom - 10).setFontSize(compact ? 9 : 11);
    this.nextText.setPosition(safe.centerX, safe.bottom - 92).setFontSize(compact ? 11 : 14);

    const shelterHeight = compact ? 240 : 285;
    const shelterWidth = shelterHeight * (640 / 480);
    const shelterX = safe.right - (compact ? 130 : 155);
    const shelterBottom = safe.bottom - 18;
    this.shelterDamaged.setPosition(shelterX, shelterBottom).setDisplaySize(shelterWidth, shelterHeight);
    this.shelterRepaired.setPosition(shelterX, shelterBottom).setDisplaySize(shelterWidth, shelterHeight);
    this.buildStation.setPosition(shelterX - 25, shelterBottom - (compact ? 90 : 105))
      .setDisplaySize(compact ? 120 : 142, compact ? 120 : 142)
      .setVisible(['build-repair', 'complete'].includes(this.snapshot?.phase));
    this.cosmo.setPosition(safe.x + (compact ? 58 : 76), safe.bottom - (compact ? 48 : 58))
      .setDisplaySize(compact ? 65 : 79, compact ? 86 : 105);
  }

  private configureRenderer() {
    const parentW = this.scale.parentSize.width || window.innerWidth;
    const parentH = this.scale.parentSize.height || window.innerHeight;
    const cssScale = Math.max(parentW / DISCOVERY_LAB_LAYOUT.width, parentH / DISCOVERY_LAB_LAYOUT.height);
    const dpr = Phaser.Math.Clamp(window.devicePixelRatio || 1, 1, 2);
    const scale = Phaser.Math.Clamp(cssScale * dpr, 1, 2);
    this.scale.scaleMode = Phaser.Scale.ENVELOP;
    this.scale.autoCenter = Phaser.Scale.NO_CENTER;
    this.scale.displaySize.setAspectMode(Phaser.Scale.ENVELOP);
    this.scale.setGameSize(Math.round(DISCOVERY_LAB_LAYOUT.width * scale), Math.round(DISCOVERY_LAB_LAYOUT.height * scale));
    this.scale.refresh();
    this.game.canvas.style.marginLeft = '0px';
    this.game.canvas.style.marginTop = '0px';
    this.cameras.main.setZoom(scale).centerOn(640, 360);
    return scale;
  }

  private applyTextResolution() {
    this.children.list.forEach(object => {
      if (object instanceof Phaser.GameObjects.Text) object.setResolution(this.renderScale);
    });
  }

  private registerLifecycle() {
    this.unsubscribeLifecycle = applicationLifecycle.subscribe({
      onSuspend: () => { this.inputEnabled = false; },
      onResume: () => { if (!this.leaving) this.inputEnabled = true; },
    });
    window.addEventListener('resize', this.handleResize);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);
  }

  private readonly handleResize = () => {
    if (this.resizeTimer !== undefined) window.clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(() => {
      if (!this.scene.isActive()) return;
      this.renderScale = this.configureRenderer();
      this.layout();
      this.applyTextResolution();
    }, 80);
  };

  private readonly returnGarden = () => {
    if (this.leaving) return;
    this.leaving = true;
    visitDiscoveryLabDistrict(DISCOVERY_LAB_DISTRICTS.pollinatorGarden);
    this.scene.start('DiscoveryLab');
  };

  private readonly returnHome = () => {
    if (this.leaving) return;
    this.leaving = true;
    SaveManager.save();
    this.scene.start('HomeHub');
  };

  private readonly tryWeatherWatch = () => {
    if (!this.completedOnEntry && this.snapshot.phase !== 'complete') return;
    if (!canEnterWeatherWatchStation(this.textures)) {
      this.feedbackText.setText('Weather Watch Station is unlocked and awaiting its runtime QA gate.');
      return audio.click();
    }
    this.leaving = true;
    this.scene.start('WeatherWatchStation');
  };

  private shutdown() {
    this.unsubscribeLifecycle?.();
    this.unsubscribeLifecycle = undefined;
    audio.leaveSoundscape({ id: 'discovery-lab' });
    window.removeEventListener('resize', this.handleResize);
    if (this.resizeTimer !== undefined) window.clearTimeout(this.resizeTimer);
    this.input.keyboard?.off('keydown', this.handleKeyDown, this);
    this.tweens.killAll();
  }
}
